import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';
import { set } from 'ember-set-helper';
import eq from 'lodash/eq';
import classNames from 'volta/helpers/class-names';

import Component from '@glimmer/component';
import BvIcon from 'volta/components/bv-icon';
import BvTooltip from 'volta/components/bv-tooltip';

import type { TTooltipSide } from 'volta/components/bv-tooltip';
import type { THueVariationString, TSize } from 'volta/utils/style-utils';

const SIZES = new Set(['sm', 'md', 'lg', 'xs', 'xl', 'xxl']);

interface BvAvatarSignature {
  Args: {
    firstName?: string;
    lastName?: string;
    login?: string;
    text?: string;
    isFocus?: boolean;
    isActive?: boolean;
    isDisabled?: boolean;
    tooltip?: string;
    tooltipSide?: TTooltipSide;
    size?: TSize;
    presence?: 'online' | 'inactive' | 'offline';
    background?: THueVariationString;
    img?: string;
    onClick?: (e: MouseEvent) => void;
  };
  Element: HTMLSpanElement;
}

export default class BvAvatarComponent extends Component<BvAvatarSignature> {
  // Properties
  // ~~~~~
  @tracked isFocus: boolean = this.args.isFocus ?? false;

  get initials() {
    const { firstName, lastName, login } = this.args;
    if (firstName && lastName) {
      return `${(firstName[0] ?? '').toUpperCase()}${(lastName[0] ?? '').toUpperCase()}`;
    }
    return login ? login.slice(0, 2).toUpperCase() : this.args.text;
  }

  get sizeClass() {
    const { size = 'md' } = this.args;
    return (size && SIZES.has(size) && `bv-avatar--${size}`) || '';
  }

  <template>
    {{! template-lint-disable require-presentational-children }}
    <span
      class={{classNames
        'bv-avatar'
        (if @isActive 'bv-avatar--active')
        (if this.isFocus 'bv-avatar--focus')
        (if @isDisabled 'bv-avatar--disabled')
        this.sizeClass
      }}
      data-test-bv-avatar-login={{@login}}
      ...attributes
      {{on 'focus' (set this 'isFocus' true)}}
      {{on 'blur' (set this 'isFocus' false)}}
    >
      <span class='bv-avatar__inner'>
        {{#if @img}}
          <span
            class='bv-avatar__imgbg'
            role='img'
            style={{htmlSafe (concat "background-image: url('" @img "')")}}
          >
            <img aria-hidden='true' src={{@img}} class='bv-avatar__img' />
          </span>
        {{else if this.initials}}
          <span
            class='bv-avatar__initials {{if @background (concat "u-bg-" @background)}}'
            data-test-avatar-initials={{this.initials}}
          >
            {{this.initials}}
          </span>
        {{else}}
          <svg
            viewBox='0 0 128 128'
            version='1.1'
            xmlns='http://www.w3.org/2000/svg'
            role='img'
            aria-label='{{@size}}'
            class='bv-avatar__default'
          >
            <title>
              {{@size}}
            </title>
            <g class='bv-avatar__default-svg'>
              <circle
                cx='64'
                cy='64'
                r='64'
                style={{htmlSafe
                  (if @background (concat 'fill:var(--bv-color-' @background ');') '')
                }}
              ></circle>
              <g>
                <path
                  d='M103,102.1388 C93.094,111.92 79.3504,118 64.1638,118 C48.8056,118 34.9294,111.768 25,101.7892 L25,95.2 C25,86.8096 31.981,80 40.6,80 L87.4,80 C96.019,80 103,86.8096 103,95.2 L103,102.1388 Z'
                ></path>
                <path
                  d='M63.9961647,24 C51.2938136,24 41,34.2938136 41,46.9961647 C41,59.7061864 51.2938136,70 63.9961647,70 C76.6985159,70 87,59.7061864 87,46.9961647 C87,34.2938136 76.6985159,24 63.9961647,24'
                ></path>
              </g>
            </g>
          </svg>
        {{/if}}
      </span>
      {{#if @presence}}
        {{#if (eq @presence 'inactive')}}
          <BvIcon
            @glyph='close'
            @size={{@size}}
            @color='white'
            class='bv-avatar__presence bv-avatar__presence--inactive'
          />
        {{else}}
          <span class='bv-avatar__presence bv-avatar__presence--{{@presence}}'></span>
        {{/if}}
      {{/if}}
      {{#if @tooltip}}
        <BvTooltip @side={{@tooltipSide}} @text={{@tooltip}} />
      {{/if}}
    </span>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvAvatar: typeof BvAvatarComponent;
    'bv-avatar': typeof BvAvatarComponent;
  }
}
